import type { IPublicClientApplication } from '@azure/msal-browser'
import { isE2EMockAuth, getE2EMockRole } from './e2eMockAuth'

export type ResolvedRole = 'admin' | 'consultant' | 'customer'

/**
 * Extracts the roles array from the MSAL token claims.
 * In E2E mock mode, returns the mock role from localStorage.
 */
export function getRolesFromToken(
  instance: IPublicClientApplication
): string[] {
  if (isE2EMockAuth()) {
    const role = getE2EMockRole()
    return role ? [role] : []
  }
  const account =
    instance.getActiveAccount() || instance.getAllAccounts()[0]
  const roles =
    (account?.idTokenClaims as unknown as { roles?: string[] })?.roles || []
  return Array.isArray(roles) ? roles : []
}

/**
 * Resolves the user's role from MSAL token claims.
 * Priority: admin > consultant (or *-consultant) > customer.
 */
export function resolveRole(
  instance: IPublicClientApplication
): ResolvedRole {
  const roles = getRolesFromToken(instance)
  if (roles.includes('admin')) return 'admin'
  if (
    roles.includes('consultant') ||
    roles.some((role) => role.endsWith('-consultant'))
  ) {
    return 'consultant'
  }
  return 'customer'
}

/**
 * Returns true if the user has consultant or admin role.
 */
export function hasConsultantRole(
  instance: IPublicClientApplication
): boolean {
  const roles = getRolesFromToken(instance)
  if (roles.includes('admin')) return true
  if (roles.includes('consultant')) return true
  return roles.some((role) => role.endsWith('-consultant'))
}
